import { defineStore } from 'pinia'
import { computed, ref, toRaw } from 'vue'
import {
  FULL_CONSENT,
  NO_CONSENT,
  actionFor,
  createConsent,
  expireConsent,
  isConsentCurrent,
  isPolicyOutdated as isRecordPolicyOutdated,
  isRefusal,
  newSubjectId,
} from '@/services/consentRecord'
import { consentService } from '@/services/consentService'
import { loadConsent, persistConsent } from '@/services/consentStorage'
import {
  OPTIONAL_CONSENT_CATEGORIES,
  type ConsentAction,
  type ConsentCategory,
  type ConsentChoices,
  type StoredConsent,
} from '@/types/consent'

/**
 * Cookie-consent state. Deliberately NOT using pinia-plugin-persistedstate:
 * the stored shape is validated strictly on load (consentRecord.parseStoredConsent)
 * and written only on an explicit decision, so persistence is hand-rolled.
 *
 * The browser copy is the enforcement source of truth - hasConsent() answers
 * synchronously from it. The server gets a copy for the audit log only, and a
 * failed upload never changes what the visitor chose. The server can make the
 * banner COME BACK (verifyOnFile: it holds no such decision) but can never grant
 * anything, and when it cannot be reached the browser's decision stands.
 *
 * Two things make the banner ask again before a choice reaches its expiry: a
 * refusal ends with the session it was given in (expireRefusal), and a decision
 * the audit log no longer holds is not trusted (verifyOnFile).
 */
export const useConsentStore = defineStore('consent', () => {
  const record = ref<StoredConsent | null>(loadConsent())
  /** Whether the full preferences modal is open (opened from the banner or the floating button). */
  const preferencesOpen = ref(false)

  /** No valid decision on file: first visit, expired, or taken under an older policy. */
  const needsDecision = computed(() => !isConsentCurrent(record.value))

  /** The choices in force, or null when there is no valid decision (= everything optional refused). */
  const activeChoices = computed<ConsentChoices | null>(() =>
    isConsentCurrent(record.value) ? record.value.categories : null,
  )

  /** True when a stored choice exists but was taken under an older cookie policy. */
  function isPolicyOutdated(): boolean {
    return isRecordPolicyOutdated(record.value)
  }

  /**
   * May storage / scripts of this category be used right now? "necessary" is
   * always true; everything else needs an explicit, current opt-in.
   */
  function hasConsent(category: ConsentCategory): boolean {
    if (category === 'necessary') return true
    return activeChoices.value?.[category] === true
  }

  async function upload(target: StoredConsent): Promise<void> {
    const sent = toRaw(target)
    try {
      await consentService.record(sent)
      // Mark synced only if this exact decision is still the current one: the
      // visitor may have decided again while the request was in flight. Compared
      // by identity, not timestamp - two decisions can share a millisecond.
      if (toRaw(record.value) === sent) {
        const done: StoredConsent = { ...sent, synced: true }
        record.value = done
        persistConsent(done)
      }
    } catch {
      // Stays unsynced; syncPending() retries on the next page load.
    }
  }

  /** True while at least one optional category is currently granted. */
  const hasGrantedConsent = computed(() => {
    const active = activeChoices.value
    return active !== null && OPTIONAL_CONSENT_CATEGORIES.some((c) => active[c])
  })

  /**
   * Records a decision: applies it locally first, then logs it to the server.
   *
   * When `action` is omitted it is inferred from the choices, with one twist:
   * refusing everything while something was granted is a WITHDRAWAL, and is
   * audited as such. The UI never has to pick between "reject" and "withdraw".
   */
  function saveConsent(choices: ConsentChoices, action?: ConsentAction): void {
    let resolved = action ?? actionFor(choices)
    if (action === undefined && resolved === 'reject_all' && hasGrantedConsent.value)
      resolved = 'withdraw'
    const next = createConsent(choices, resolved, record.value?.subjectId ?? newSubjectId())
    record.value = next
    persistConsent(next)
    preferencesOpen.value = false
    void upload(next)
  }

  function acceptAll(): void {
    saveConsent(FULL_CONSENT, 'accept_all')
  }

  /** Refuses everything optional (audited as a withdrawal if something was granted). */
  function rejectAll(): void {
    saveConsent(NO_CONSENT)
  }

  /** Revokes everything optional, always audited as a withdrawal. */
  function withdrawConsent(): void {
    saveConsent(NO_CONSENT, 'withdraw')
  }

  /** Retries uploading a decision the server has not acknowledged yet. */
  async function syncPending(): Promise<void> {
    if (record.value && !record.value.synced) await upload(record.value)
  }

  /** Stops honouring the decision in force so the banner asks again, keeping it on file for its browser id. */
  function expireCurrent(current: StoredConsent): void {
    const expired = expireConsent(toRaw(current))
    record.value = expired
    persistConsent(expired)
  }

  /**
   * A refusal lasts as long as the session it was given in. Called when a session ends
   * (logout, expired token): a "no" is not a permanent answer, so the banner asks again at
   * the next sign-in. The decision stays on file, expired, so the browser keeps its id and
   * the audit trail goes on. An acceptance is untouched.
   */
  function expireRefusal(): void {
    const current = record.value
    if (isConsentCurrent(current) && isRefusal(current)) expireCurrent(current)
  }

  /**
   * The decision lives in this browser; the server only keeps the evidence. When the server
   * answers that it holds no such decision (rows purged, a database reset) the banner asks
   * again. Only that explicit "no" changes anything: an unreachable or older API, a 429 or
   * any other failure is "unknown" and the decision stands - enforcement never depends on
   * the network.
   */
  async function verifyOnFile(): Promise<void> {
    const checked = record.value
    // A decision still waiting to be uploaded is not missing from the log yet:
    // syncPending() is what settles that.
    if (!isConsentCurrent(checked) || !checked.synced) return

    let onFile: boolean
    try {
      onFile = await consentService.verify(toRaw(checked))
    } catch {
      return
    }
    // The visitor may have decided again while the request was in flight.
    if (onFile || toRaw(record.value) !== toRaw(checked)) return
    expireCurrent(checked)
  }

  function openPreferences(): void {
    preferencesOpen.value = true
  }

  function closePreferences(): void {
    preferencesOpen.value = false
  }

  return {
    record,
    preferencesOpen,
    needsDecision,
    activeChoices,
    hasGrantedConsent,
    isPolicyOutdated,
    hasConsent,
    saveConsent,
    acceptAll,
    rejectAll,
    withdrawConsent,
    syncPending,
    expireRefusal,
    verifyOnFile,
    openPreferences,
    closePreferences,
  }
})
